import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Api from '../../component/Api';
import logo from '../../Assets/img/logo-small.png';
import { useUmsRoute } from './hooks/useUmsRoute';
import '../../Assets/css/vendors/bootstrap.css';
import { ToastContainer, toast } from 'react-toastify';
import { handleAuthSuccess } from './services/auth'

function Login() {
    const { http } = Api();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        user_id: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [errors, setErrors] = useState({
        user_id: '',
        password: '',
        login: ''
    });

    const { umsUrl, handleClick } = useUmsRoute();

    const validateUserId = (userId) => {
        if (!userId || userId.trim() === '') {
            return 'Please enter valid email';
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userId)) {
            return 'Please enter a valid email format';
        }
        return '';
    };

    const validatePassword = (password) => {
        if (!password || password.trim() === '') {
            return 'Please enter valid password';
        }
        return '';
    };

    const validateForm = () => {
        const userIdError = validateUserId(formData.user_id);
        const passwordError = validatePassword(formData.password);

        setErrors({
            user_id: userIdError,
            password: passwordError,
            login: ''
        });

        if (userIdError) {
            document.getElementById('user_id')?.focus();
            return false;
        }
        if (passwordError) {
            document.getElementById('password')?.focus();
            return false;
        }

        return true;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        if (errors.login) {
            setErrors(prev => ({
                ...prev,
                login: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await http.post('/is/login', {
                email: formData.user_id,
                password: formData.password
            });

            if (response.data?.token) {
                handleAuthSuccess(response.data.token);
                navigate('/is/home');
            } else {
                setErrors(prev => ({
                    ...prev,
                    login: 'Login failed. Please try again.'
                }));
            }
        } catch (error) {

            const hasStatusCode = error.response?.data?.status_code;
    
            const message = hasStatusCode
            ? error.response?.data?.message ||
            error.response?.data?.error ||
            'Invalid Username or Password'
            : 'Something went wrong';
    
            setErrors(prev => ({
                ...prev,
                login: message
            }));

        } finally {
            setIsLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    useEffect(() => {
        const hash = window.location.hash;
        const queryString = hash.includes('?') ? hash.split('?')[1] : '';
        const params = new URLSearchParams(queryString);

        const success = params.get('success');
        const error = params.get('error');

        if (success) {
            toast.success(success);
        }

        if (error) {
            toast.error(error);
        }
    }, []);

    return (
        <>
            <ToastContainer />
            <div className="container-fluid bg-gradient-primary">
                <div className="row justify-content-center flex-grow-1">
                    <div className="col-xl-5 col-lg-12 col-md-9">
                        <div className="o-hidden border-0 shadow-lg my-5 brd-rad bk-clr">
                            <div className="card-body p-0">
                                <div className="row">
                                    <div className="col-lg-12">
                                        <div className="p-5">
                                            <div className="circle23">
                                                <img className="img-fluid for-light" src={logo} alt="MGU Logo" />
                                            </div>
                                            <div className="heading12 text-center">Sign in to Your Account</div>
                                            <br />

                                            <form onSubmit={handleSubmit} noValidate>
                                                <div className="user mt-2">
                                                    {errors.login && (
                                                        <div className="alert alert-danger" role="alert">
                                                            <p style={{ textAlign: 'center', textJustify: 'auto' }}>
                                                                {errors.login}
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div className="form-group">
                                                        <input
                                                            type="email"
                                                            className="form-control"
                                                            id="user_id"
                                                            name="user_id"
                                                            placeholder="Email"
                                                            value={formData.user_id}
                                                            onChange={handleInputChange}
                                                            disabled={isLoading}
                                                            style={{ width: '100%' }}
                                                            autoComplete="username"
                                                        />
                                                        {errors.user_id && (
                                                            <p className="text-danger">{errors.user_id}</p>
                                                        )}
                                                    </div>
                                                    <br />

                                                    <div className="form-group">
                                                        <div className="form-input position-relative">
                                                            <div style={{ position: 'relative' }}>
                                                                <input
                                                                    type={showPassword ? "text" : "password"}
                                                                    className="form-control form-control-user"
                                                                    id="password"
                                                                    name="password"
                                                                    placeholder="Password"
                                                                    value={formData.password}
                                                                    onChange={handleInputChange}
                                                                    disabled={isLoading}
                                                                    style={{ width: '100%' }}
                                                                    autoComplete="current-password"
                                                                />
                                                                <span
                                                                    className="show-hide"
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: '50%',
                                                                        transform: 'translateY(-50%)',
                                                                        right: '10px',
                                                                        cursor: isLoading ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                    onClick={!isLoading ? togglePasswordVisibility : undefined}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onKeyPress={(e) => {
                                                                        if (e.key === 'Enter' && !isLoading) {
                                                                            togglePasswordVisibility();
                                                                        }
                                                                    }}
                                                                >
                                                                    {showPassword ? (
                                                                        <i className="fa fa-eye-slash fa-stl"></i>
                                                                    ) : (
                                                                        <i className="fa fa-eye fa-stl"></i>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {errors.password && (
                                                            <p className="text-danger">{errors.password}</p>
                                                        )}
                                                    </div>

                                                    <div className="st-lgn-pdg1" style={{ textAlign: 'center', marginTop: '20px' }}>
                                                        <a href="#/sfa/sfaforgetpassword" className="forgot-password23">
                                                            Forgot Password..?
                                                        </a>
                                                    </div>

                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary btn-user btn-block bt mt-3"
                                                        style={{ width: '100%' }}
                                                        disabled={isLoading}
                                                    >
                                                        <h6>{isLoading ? 'Logging in...' : 'Login'}</h6>
                                                    </button>
                                                </div>
                                            </form>

                                            <button
                                                id="ums_login"
                                                onClick={handleClick}
                                                className="btn w-100 my-4 mb-2 text-center"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    border: "1px solid #ccc",
                                                    borderRadius: "5px",
                                                    padding: "8px",
                                                    width: "fit-content",
                                                    margin: "auto",
                                                    cursor: umsUrl === "#" ? "not-allowed" : "pointer",
                                                    opacity: umsUrl === "#" ? 0.6 : 1
                                                }}
                                            >
                                                <img src={logo} alt="Icon" style={{ width: 20, height: 20 }} />
                                                <span
                                                    style={{
                                                        fontSize: 13,
                                                        color: "#333",
                                                        marginLeft: 8,
                                                        textTransform: "capitalize"
                                                    }}
                                                >
                                                    Login With University Management System
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <footer className="footer foot-wt">
                    <div className="container my-auto">
                        <div className="copyright text-center my-auto">
                            <span style={{ color: 'black' }}>
                                <a
                                    href="https://www.mgu.ac.in/"
                                    style={{ color: 'black' }}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Mahatma Gandhi University, Priyadarsini Hills, Kottayam, Kerala, India - 686560
                                </a>
                            </span>
                        </div>
                    </div>
                </footer>
            </div>
        </>
    );
}

export default Login;